import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2, RefreshCw, X } from 'lucide-react'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { ApiError } from '#/lib/api'
import { cancelTransaction, payWithSnap, useInvalidateBilling, usePaymentsConfig, useTransactions, verifyTransaction, type TransactionDto, type TxStatus } from '#/lib/api-billing'
import { authClient } from '#/lib/auth-client'
import { fmtCurrency, fmtDateTime } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/transactions')({
  component: Transactions,
  validateSearch: (s: Record<string, unknown>): { order?: string } => ({ order: typeof s.order === 'string' && s.order ? s.order : undefined }),
})

export const txStatusLabel = (s: TxStatus) =>
  s === 'paid' ? m.tx_status_paid() : s === 'pending' ? m.tx_status_pending() : s === 'deny' ? m.tx_status_deny() : s === 'cancel' ? m.tx_status_cancel() : s === 'expire' ? m.tx_status_expire() : s === 'refund' ? m.tx_status_refund() : s === 'failed' ? m.tx_status_failed() : s
const statusClass = (s: TxStatus) => (s === 'paid' ? 'bg-status-done/15 text-status-done border-transparent' : s === 'pending' ? 'bg-status-progress/15 text-status-progress border-transparent' : s === 'deny' || s === 'failed' ? 'bg-status-failed/15 text-status-failed border-transparent' : '')
const itemLabel = (t: TransactionDto) => (t.kind === 'addon' ? (t.addon === 'prd' ? m.tx_addon_prd() : m.tx_addon_chat()) : (t.planName ?? '—'))

function Transactions() {
  const { order } = Route.useSearch()
  const navigate = useNavigate()
  const { data: session, isPending } = authClient.useSession()
  useEffect(() => { if (!isPending && !session?.user) navigate({ to: '/sign-in', search: { redirect: '/transactions' } }) }, [isPending, session, navigate])
  const { data } = useTransactions(Boolean(session?.user))
  const { data: cfg } = usePaymentsConfig()
  const invalidate = useInvalidateBilling()
  const [busy, setBusy] = useState<string | null>(null)

  const check = async (t: TransactionDto, simulate?: 'settlement') => {
    setBusy(t.id)
    try {
      const r = await verifyTransaction({ orderId: t.orderId, simulate })
      invalidate()
      if (r.status === 'paid') toast.success(m.billing_paid_success())
      else toast.message(m.tx_updated({ status: txStatusLabel(r.status) }))
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined
      toast.error(code === 'gateway_not_configured' ? m.billing_gateway_not_configured() : e instanceof ApiError ? e.message : m.common_error_generic())
    } finally {
      setBusy(null)
    }
  }
  const payAgain = async (t: TransactionDto) => {
    if (!cfg?.midtrans.configured || !t.snapToken) return check(t)
    setBusy(t.id)
    try {
      await payWithSnap(cfg.midtrans, t.snapToken, {
        onSuccess: () => void check(t),
        onPending: () => toast.message(m.billing_paid_pending()),
        onError: () => toast.error(m.billing_paid_error()),
        onClose: () => toast.message(m.billing_paid_closed()),
      })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : m.common_error_generic())
    } finally {
      setBusy(null)
    }
  }
  const cancel = async (t: TransactionDto) => {
    setBusy(t.id)
    try {
      const r = await cancelTransaction(t.orderId)
      invalidate()
      toast.message(m.tx_updated({ status: txStatusLabel(r.status) }))
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    } finally {
      setBusy(null)
    }
  }
  // Landed here from the Midtrans "finish" redirect → refresh that order once
  useEffect(() => {
    const t = data?.transactions.find((x) => x.orderId === order)
    if (t && t.status === 'pending' && cfg?.midtrans.configured) void check(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, data?.transactions.length, cfg?.midtrans.configured])

  if (!session?.user) return null
  return (
    <>
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-3xl space-y-4">
          <div>
            <h1 className="text-3xl font-bold">{m.tx_title()}</h1>
            <p className="text-sm text-muted-foreground">{m.tx_hint()}</p>
          </div>
          {!data?.transactions.length ? (
            <p className="text-muted-foreground">{m.tx_empty()}</p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border bg-card">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr className="border-b">
                    <th className="px-4 py-2 font-medium">{m.tx_col_date()}</th>
                    <th className="px-2 py-2 font-medium">{m.tx_col_plan()}</th>
                    <th className="px-2 py-2 font-medium">{m.tx_col_provider()}</th>
                    <th className="px-2 py-2 text-right font-medium">{m.tx_col_amount()}</th>
                    <th className="px-2 py-2 font-medium">{m.tx_col_status()}</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t) => {
                    const pending = t.status === 'pending'
                    const isBusy = busy === t.id
                    const simulate = pending && cfg?.dev && !(t.provider === 'midtrans' ? cfg.midtrans.configured : cfg.paddle.configured)
                    return (
                      <tr key={t.id} className={order === t.orderId ? 'border-t bg-primary/5' : 'border-t'}>
                        <td className="px-4 py-2.5 align-top whitespace-nowrap">
                          <div>{fmtDateTime(t.createdAt)}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">{t.orderId}</div>
                        </td>
                        <td className="px-2 py-2.5 align-top">{itemLabel(t)}</td>
                        <td className="px-2 py-2.5 align-top">
                          <Badge variant="outline" className="capitalize">{t.provider}</Badge>
                          {t.paymentType ? <div className="mt-0.5 text-[11px] text-muted-foreground">{t.paymentType}</div> : null}
                        </td>
                        <td className="px-2 py-2.5 text-right align-top tabular-nums whitespace-nowrap">
                          <div className="font-medium">{fmtCurrency(t.grossAmount, t.currency)}</div>
                          {t.discountAmount > 0 ? <div className="text-[11px] text-status-done">{m.tx_discount({ amount: fmtCurrency(t.discountAmount, t.currency) })}</div> : null}
                        </td>
                        <td className="px-2 py-2.5 align-top"><Badge variant="secondary" className={statusClass(t.status)}>{txStatusLabel(t.status)}</Badge></td>
                        <td className="px-4 py-2.5 text-right align-top whitespace-nowrap">
                          {pending && !simulate ? (
                            <div className="inline-flex gap-1">
                              {t.provider === 'midtrans' && t.snapToken ? <Button size="sm" variant="default" className="h-7" disabled={isBusy} onClick={() => payAgain(t)}>{isBusy ? <Loader2 className="size-3.5 animate-spin" /> : null} {m.tx_pay_again()}</Button> : null}
                              <Button size="sm" variant="secondary" className="h-7" disabled={isBusy} onClick={() => check(t)}><RefreshCw className="size-3.5" /> {m.tx_check_status()}</Button>
                              <Button size="sm" variant="ghost" className="h-7" disabled={isBusy} onClick={() => cancel(t)} aria-label={m.tx_cancel()}><X className="size-3.5" /></Button>
                            </div>
                          ) : null}
                          {simulate ? (
                            <div className="inline-flex gap-1">
                              <Button size="sm" variant="outline" className="h-7 border-dashed" disabled={isBusy} onClick={() => check(t, 'settlement')}>{isBusy ? <Loader2 className="size-3.5 animate-spin" /> : null} {m.tx_simulate()}</Button>
                              <Button size="sm" variant="ghost" className="h-7" disabled={isBusy} onClick={() => cancel(t)} aria-label={m.tx_cancel()}><X className="size-3.5" /></Button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </>
  )
}
